import { mergeProps } from '@base-ui/react/merge-props';
import { useRender } from '@base-ui/react/use-render';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from 'lib/cn';

const badgeVariants = cva(
  "group/badge inline-block h-4 w-fit shrink-0 truncate rounded-full border border-transparent px-1.5 align-middle text-subheadline-emphasized transition-all focus-ring has-data-[icon=inline-end]:pr-1 has-data-[icon=inline-start]:pl-1 [&_[data-icon=inline-end]]:ml-1 [&_[data-icon=inline-start]]:mr-1 [&_svg]:inline-block [&_svg]:pointer-events-none [&_svg]:align-middle [&_svg:not([class*='size-'])]:size-3",
  {
    variants: {
      variant: {
        neutral:
          'bg-fill-quaternary text-foreground [a&]:hover:overlay-fill-quaternary',
        secondary:
          'bg-fill-quaternary text-foreground-secondary [a&]:hover:overlay-fill-quaternary [a&]:hover:text-foreground',
        brand:
          'bg-accent-brand text-accent-brand-foreground [a&]:hover:overlay-fill-quaternary',
        success:
          'bg-intent-success text-intent-success-foreground [a&]:hover:overlay-fill-quaternary',
        warning:
          'bg-intent-warning text-intent-warning-foreground [a&]:hover:overlay-fill-quaternary',
        destructive:
          'bg-intent-destructive-fill text-intent-destructive-fill-foreground [a&]:hover:overlay-fill-quaternary',
      },
    },
    defaultVariants: {
      variant: 'neutral',
    },
  },
);

export type BadgeVariant = NonNullable<
  VariantProps<typeof badgeVariants>['variant']
>;

export const Badge = ({
  className,
  variant = 'neutral',
  render,
  ...props
}: useRender.ComponentProps<'span'> & VariantProps<typeof badgeVariants>) =>
  useRender({
    defaultTagName: 'span',
    props: mergeProps<'span'>(
      { className: cn(badgeVariants({ variant }), className) },
      props,
    ),
    render,
    state: {
      slot: 'badge',
      variant,
    },
  });
Badge.displayName = 'Badge';

export { badgeVariants };
